import { useCallback, useMemo, useRef } from 'react';
import { Combobox } from '@library/components/selects';
import { MemberConfirmationModal } from '@bo-components/members/modal';
import { activationMessage } from '@bo-constants/member/memberCopy';
import { cn } from '@library/component-utils/cn';
import { getMemberDeactivationOptions } from '@bo-utils/members/members';
import { useMemberActivation } from '@bo-hooks/members';

export interface MemberUpdateActivationModalProps {
  isDeactivated: boolean;
  memberId: string;
  onClose: () => void;
}

export const MemberUpdateActivationModal = ({
  isDeactivated,
  memberId,
  onClose
}: MemberUpdateActivationModalProps) => {
  const initialIsDeactivated = useRef(isDeactivated).current;

  const {
    deactivationReason,
    isActivationError,
    isActivationLoading,
    isActivationSuccess,
    onDeactivationReasonChange,
    onSubmit
  } = useMemberActivation({ isDeactivated, memberId });

  const deactivationReasonOptions = useMemo(
    () => getMemberDeactivationOptions(),
    []
  );

  const { content, fail, success, title } = useMemo(
    () => activationMessage(initialIsDeactivated),
    [initialIsDeactivated]
  );

  const confirmationCopy = initialIsDeactivated ? 'Reactivate' : 'Deactivate';
  const disableConfirmButton =
    isActivationLoading || (!isDeactivated && !deactivationReason);
  const showConfirmationMessage = isActivationError || isActivationSuccess;

  const handleSubmit = useCallback(async () => {
    await onSubmit();
  }, [onSubmit]);

  const UpdateActivationStatus = useMemo(() => {
    return (
      <div className="text-soClean-180 flex flex-col text-sm">
        <p>{content}</p>
        {!initialIsDeactivated && (
          <Combobox
            closeMenuOnSelect
            disabled={isActivationLoading}
            id="memberDeactivationReason"
            isMulti={false}
            label="Select a reason to deactivate this member"
            labelClassName="text-soClean-180 text-sm leading-8"
            onChange={onDeactivationReasonChange}
            optionClassName="font-heebo text-sm px-3 py-1 rounded bg-soFresh-110 capitalize"
            options={deactivationReasonOptions}
            placeholder="Select deactivation reason"
            value={deactivationReason}
          />
        )}
      </div>
    );
  }, [
    content,
    deactivationReason,
    deactivationReasonOptions,
    initialIsDeactivated,
    isActivationLoading,
    onDeactivationReasonChange
  ]);

  const ConfirmationMessage = useMemo(() => {
    const confirmationMessage = isActivationSuccess ? success : fail;

    return (
      <p
        className={cn('text-soClean-180 text-sm', {
          'text-roses': isActivationError
        })}
      >
        {confirmationMessage}
      </p>
    );
  }, [fail, isActivationError, isActivationSuccess, success]);

  return (
    <MemberConfirmationModal
      cancelCopy="Cancel"
      childrenClassName="justify-evenly min-h-32"
      className="w-[25rem]"
      confirmationCopy={confirmationCopy}
      handleConfirm={handleSubmit}
      isConfirmDisabled={disableConfirmButton}
      isOpen
      label={title}
      onCancelModal={onClose}
      showConfirmationMessage={showConfirmationMessage}
    >
      {!showConfirmationMessage ? UpdateActivationStatus : ConfirmationMessage}
    </MemberConfirmationModal>
  );
};
